/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import React from 'react';
import { VStack, Heading } from 'chakra-v3';
import { Text } from '../Text';
import { Input } from '../inputs/Input';
import { Button } from '../buttons/Index';
import { useForm } from '../../helpers/parameter-renderer/hooks/useForm';
import type { ParameterForm } from '@karya/parameter-specs';

interface EnterUpiProps {
  onSubmit: (upiId: string, name: string) => void;
  isLoading: boolean;
  initialName?: string;
  initialUpiId?: string;
}

type UpiFormData = {
  upiId: string;
  name: string;
};

const upiFormParameters: ParameterForm<UpiFormData> = [
  {
    label: 'UPI Details',
    required: true,
    parameters: [
      {
        id: 'name',
        label: 'Account Holder Name',
        type: 'string',
        required: true,
        placeholder: 'Account Holder Name',
        pattern: '^[A-Za-z][A-Za-z\\s]*[A-Za-z]$',
      },
      {
        id: 'upiId',
        label: 'UPI ID',
        type: 'string',
        required: true,
        placeholder: 'Enter UPI ID (e.g. user@bank)',
        pattern: '^[a-zA-Z0-9][a-zA-Z0-9._-]*@[a-zA-Z0-9]+$',
      },
    ],
  },
];

export const EnterUpi: React.FC<EnterUpiProps> = ({ onSubmit, isLoading, initialName = '', initialUpiId = '' }) => {
  const { ctx, handleSubmit } = useForm<UpiFormData>({
    parameters: upiFormParameters,
    initialState: {
      name: initialName,
      upiId: initialUpiId,
    },
    onSubmit: (data) => {
      onSubmit(data.upiId, data.name);
    },
    sectionless: true,
  });

  return (
    <VStack gap={4} align="start" maxW="300px">
      <Heading as="h2">Enter UPI Details</Heading>
      <Text variant="body" color="text.primary">
        To receive payments, please enter your UPI ID and Name.
      </Text>

      <form onSubmit={handleSubmit} style={{ width: '100%' }}>
        <VStack gap={3} w="100%" align="stretch">
          <Input
            id="name"
            type="text"
            placeholder="Account Holder Name"
            value={ctx.form.name ?? ''}
            onChange={ctx.handleChange}
            disabled={isLoading}
            isInvalid={!!ctx.errors.name}
            errorText={ctx.errors.name}
          />

          <Input
            id="upiId"
            type="text"
            placeholder="Enter UPI ID (e.g. user@bank)"
            value={ctx.form.upiId ?? ''}
            onChange={ctx.handleChange}
            disabled={isLoading}
            isInvalid={!!ctx.errors.upiId}
            errorText={ctx.errors.upiId}
          />

          <Button type="submit" variant="cta" disabled={isLoading || !ctx.form.upiId || !ctx.form.name} w="100%">
            {isLoading ? 'Verifying...' : 'Verify UPI'}
          </Button>
        </VStack>
      </form>
    </VStack>
  );
};
